<template>
  <div class="px-10 py-20 bg-primary-50 rounded">
    <p class="mb-20 text-xl font-bold text-primary-500" v-html="lead" />
    <p
      class="mb-20 text-xl font-bold text-primary-500"
      v-html="perhapsLookHere"
    />
    <ul>
      <li class="mb-10">
        <a :href="prozOnlineUrl" target="_blank" class="link with-icon">
          {{ $texts('events.prozOnline', 'Programmzeitung') }}
          <span class="sr-only">
            {{
              $texts(
                'accessibility.externalLinkText',
                'Externer Link, wird in einem neuen Tab oder Fenster geöffnet',
              )
            }}
          </span>
        </a>
      </li>
      <li>
        <a :href="baselTourismUrl" target="_blank" class="link with-icon">
          {{ $texts('events.baselTourism', 'Basel Tourismus') }}
        </a>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
const props = defineProps<{
  forDate: Date
}>()

const { $texts } = useNuxtApp()

const monthsLong = [
  $texts('monthLong.january', 'Januar'),
  $texts('monthLong.february', 'Februar'),
  $texts('monthLong.march', 'März'),
  $texts('monthLong.april', 'April'),
  $texts('monthLong.may', 'Mai'),
  $texts('monthLong.june', 'Juni'),
  $texts('monthLong.july', 'Juli'),
  $texts('monthLong.august', 'August'),
  $texts('monthLong.september', 'September'),
  $texts('monthLong.october', 'Oktober'),
  $texts('monthLong.november', 'November'),
  $texts('monthLong.december', 'Dezember'),
]

const startDateIso = computed(() => {
  return (
    props.forDate.getFullYear() +
    '-' +
    (props.forDate.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    props.forDate.getDate().toString().padStart(2, '0')
  )
})

const datePlusOneDayIso = computed(() => {
  // Add one day to the date to get the end date.
  const endDate = new Date(props.forDate)
  endDate.setDate(endDate.getDate() + 1)

  return (
    endDate.getFullYear() +
    '-' +
    (endDate.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    endDate.getDate().toString().padStart(2, '0')
  )
})

const prozOnlineUrl = computed(() => {
  return (
    'https://www.proz.online/Veranstaltungen?fromDate=' + startDateIso.value
  )
})

const startDateReadableFull = computed(() => {
  const day = props.forDate.getDate()
  const month = monthsLong[props.forDate.getMonth()]
  const year = props.forDate.getFullYear()

  return `<span class="text-green-800">${day}. ${month} ${year}</span>`
})

const lead = computed(() => {
  const text = $texts(
    'events.noEventsAtDate',
    'Am @date finden keine Veranstaltungen statt. Bitte überprüfen Sie ihre Filtereinstellungen.',
  )

  return text.replace('@date', startDateReadableFull.value)
})

const perhapsLookHere = computed(() => {
  const text = $texts(
    'events.perhapsLookHere',
    'Andernfalls finden Sie hier Informationen zu Anlässen am @date.',
  )

  return text.replace('@date', startDateReadableFull.value)
})

const baselTourismUrl = computed(() => {
  // This section is taken from an example URL.
  // Most if it is predefines stuff that the
  // Basel Tourism event calendar simply appends.
  const params = btoa(
    JSON.stringify({
      predefinedParams: {},
      apiQuery: {
        include: ['mainImage', 'price'],
        sorting: {
          property: 'date',
        },
        unlicensed: true,
        includeRangeDates: false,
        includeDateIntervals: false,
        filter: {
          clientIncludingManaged: 'current',
          element: 'events',
          date: {
            after: startDateIso.value,
            before: datePlusOneDayIso.value,
          },
          highlight: false,
          categories: [],
          areas: [],
          cities: [],
          dynamicFields: {},
          search: '',
          location: {},
          distanceFrom: {},
          tip: [],
        },
        preview: '266x312',
        pagination: {
          page: 1,
          pageSize: 12,
        },
        withChannelContext: true,
        language: 'de',
      },
      guiQuery: {
        isSecondaryList: false,
        filter: {
          highlight: false,
          categories: [],
          areas: [],
          cities: [],
          dynamicFields: {},
          search: '',
          location: {},
          date: {
            after: startDateIso.value,
            before: datePlusOneDayIso.value,
          },
          distanceFrom: {},
          tip: [],
        },
      },
    }),
  )

  return 'https://www.basel.com/de/veranstaltungen#/event?params=' + params
})
</script>
